/**
 * 区画詳細「添付書類」セクション。
 *
 * - 物件書類の「その他」と同じ UX (アコーディオン無し版)。
 * - 各書類グループは shared-components の CustomDocumentPreview (type="multiple")
 *   をそのまま使う。スタイル/挙動は物件書類と完全に同じ。
 * - データは Unit.documents (UnitDocument[]) を「label」でグルーピング。
 *   既存ファイルは s3_key (URL)、新規アップロードは File オブジェクトで一旦 form 内に保持し、
 *   保存時に親 (UnitTabForm) が一括で BE へ送信する想定。
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import {
  CustomSectionTitle,
  CustomButton,
  CustomDocumentPreview,
} from "@ippon/shared-components";
import type { UnitDocument } from "@ippon/shared-types";
import { Can } from "@/components/auth/Can";

interface Props {
  documents: UnitDocument[];
  onChange: (next: UnitDocument[]) => void;
  /**
   * 親フォームのレイアウト幅 (区画詳細ページでは "600px")。
   * 「ラベル入力 + 追加ボタン」を合わせてこの幅に収める。
   */
  inputWidth?: string;
  /** セクション見出しのテキスト (省略時は「添付書類」)。例: 「関連資料」 */
  title?: string;
  /** 見出しを描画しない (親側で見出しを出す or 不要なケース) */
  hideTitle?: boolean;
}

const groupKeyForLabel = (label: string) =>
  `doc_${label.replace(/[^a-zA-Z0-9_]/g, "_")}`;

const documentSignature = (documents: UnitDocument[]) =>
  JSON.stringify(documents.map((d) => `${d.label}|${d.file_name}|${d.s3_key}`));

const UnitOtherDocuments: React.FC<Props> = ({
  documents,
  onChange,
  inputWidth,
  title = "添付書類",
  hideTitle,
}) => {
  const [newLabel, setNewLabel] = useState("");

  /** ラベル別グルーピング (順序を保ったまま) */
  const groups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, UnitDocument[]>();
    documents.forEach((d) => {
      const key = (d.label ?? "未分類").trim() || "未分類";
      if (!map.has(key)) {
        order.push(key);
        map.set(key, []);
      }
      map.get(key)!.push(d);
    });
    return order.map((label) => ({ label, files: map.get(label) || [] }));
  }, [documents]);

  /** CustomDocumentPreview 用のローカル useForm。
   *  各グループに name=`doc_<sanitizedLabel>` を割り当て、watch で値変化を拾う */
  const form = useForm<Record<string, any>>({ defaultValues: {} });

  // 既存ファイル URL マップ (ラベル -> s3_key 配列)。
  // documents が更新されたタイミングで CustomDocumentPreview に渡す
  const existingByLabel = useMemo(() => {
    const m: Record<string, string[]> = {};
    groups.forEach((g) => {
      m[groupKeyForLabel(g.label)] = g.files
        .map((f) => f.s3_key)
        .filter(Boolean);
    });
    return m;
  }, [groups]);

  // フォーム → Unit.documents への同期。
  // 親に逆流させて無限ループしないよう、前回値と等価判定してから onChange を呼ぶ
  const documentsSignature = useMemo(
    () => documentSignature(documents),
    [documents],
  );
  const prevSyncedRef = useRef<string>(documentsSignature);
  useEffect(() => {
    prevSyncedRef.current = documentsSignature;
  }, [documentsSignature]);
  const allValues = form.watch();
  useEffect(() => {
    const next: UnitDocument[] = [];
    groups.forEach((g) => {
      const fieldKey = groupKeyForLabel(g.label);
      const raw = allValues?.[fieldKey];
      const items: any[] = Array.isArray(raw) ? raw : [];
      let pushed = 0;
      items.forEach((it, idx) => {
        if (typeof it === "string") {
          const matched = g.files.find((f) => f.s3_key === it);
          next.push(
            matched ?? {
              document_id: `kept_${
                typeof crypto !== "undefined" && crypto.randomUUID
                  ? crypto.randomUUID()
                  : `${Date.now()}_${idx}`
              }`,
              category: "other",
              label: g.label,
              file_name: it.split("/").pop() ?? it,
              s3_key: it,
              uploaded_at: new Date().toISOString(),
            },
          );
          pushed += 1;
        } else if (it instanceof File) {
          // 新規アップロード: File を _pendingFile に保持して親に渡す。
          // 親 (UnitTabForm 側の onSubmit) が processFileArray で S3 にアップロードし
          // s3_key に置換してから updateUnit を呼ぶ。
          next.push({
            document_id: `new_${g.label}_${idx}_${it.name}`,
            category: "other",
            label: g.label,
            file_name: it.name,
            s3_key: "",
            uploaded_at: new Date().toISOString(),
            // FE 内部の搬送用フィールド (BE 送信前にヘルパーで除去)
            _pendingFile: it,
          } as UnitDocument & { _pendingFile?: File });
          pushed += 1;
        }
      });
      // フォーム値が空のグループ (= ラベル追加直後でまだファイルがない / CustomDocumentPreview の
      // useEffect が走る前) は、g.files にあるダミー or _pendingFile を保持してグループを残す。
      // 残さないと「ラベルだけ追加した状態」が即座に消えてしまう。
      if (pushed === 0 && g.files.length > 0) {
        g.files.forEach((f) => next.push(f));
      }
    });
    const sig = documentSignature(next);
    if (sig === prevSyncedRef.current) return;
    prevSyncedRef.current = sig;
    onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(allValues), groups]);

  const handleAddGroup = () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    if (groups.some((g) => g.label === trimmed)) {
      setNewLabel("");
      return;
    }
    // ラベルだけのダミー (file_name 空) を入れて UI に枠を出す。
    onChange([
      ...documents,
      {
        document_id: `tmp_${Date.now()}`,
        category: "other",
        label: trimmed,
        file_name: "",
        s3_key: "",
        uploaded_at: new Date().toISOString(),
      },
    ]);
    setNewLabel("");
  };

  const handleRemoveGroup = (label: string) => {
    onChange(documents.filter((d) => (d.label ?? "未分類") !== label));
  };

  return (
    <Box>
      {!hideTitle && <CustomSectionTitle title={title} size="small" />}
      <Stack spacing={2} sx={{ mt: 2 }}>
        {groups.map((group) => {
          const fieldKey = groupKeyForLabel(group.label);
          return (
            <Box key={group.label} sx={{ position: "relative" }}>
              <CustomDocumentPreview
                name={fieldKey}
                label={group.label}
                register={form.register}
                setValue={form.setValue}
                watch={form.watch}
                errors={form.formState.errors}
                type="multiple"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                existingDocuments={existingByLabel[fieldKey] || []}
                onRemove={() => handleRemoveGroup(group.label)}
              />
            </Box>
          );
        })}

        {/* 新しい書類グループの追加 (物件書類「その他」と同じ位置/UI)。
            inputWidth が渡された場合 (区画詳細ページ)、ラベル入力 + ボタンを
            合わせてその幅に収める。 */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ width: inputWidth, maxWidth: "100%" }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="書類名 (例: 仕様書 / 取扱説明書)"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddGroup();
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  borderRadius: "8px",
                  height: "36px",
                  fontSize: 12,
                },
              }}
            />
          </Box>
          <Can action="property:edit">
            <CustomButton
              label="追加"
              buttonCategory="submit"
              onClick={handleAddGroup}
              disabled={!newLabel.trim()}
            />
          </Can>
        </Stack>
      </Stack>
    </Box>
  );
};

export default UnitOtherDocuments;
